import React, { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import { LineChart } from "@mui/x-charts/LineChart";
import CoreAPI from "../../../../../services/core";

dayjs.extend(buddhistEra);

const Backdrop = styled.div`
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.35);
    display: flex; justify-content: flex-end;
    z-index: 1000;
`;
const Drawer = styled.div`
    width: 480px; max-width: 100vw; height: 100vh;
    background: #fff; padding: 20px; overflow-y: auto;
    position: relative;
    h2 { color: #25368D; font-size: 18px; font-weight: 700; margin: 0 0 8px; }
    .meta { color: #838BB4; font-size: 13px; margin-bottom: 20px; }
    .tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
    .tile { background: #F5F7FB; border-radius: 8px; padding: 12px; }
    .tile .l { color: #505050; font-size: 11px; }
    .tile .v { color: #25368D; font-size: 18px; font-weight: 700; }
    .section-h { color: #25368D; font-size: 14px; font-weight: 600; margin: 16px 0 8px; }
    .activity { font-size: 13px; }
    .activity .row { display: flex; gap: 8px; padding: 8px 0; border-bottom: 1px solid #F5F5F5; }
    .activity .badge { padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; }
    .activity .badge.sub { background: #25368D22; color: #25368D; }
    .activity .badge.att { background: #FBE25033; color: #8a7400; }
    .close { position: absolute; top: 16px; right: 16px; background: none; border: 0; font-size: 22px; cursor: pointer; }
    .empty { color: #838BB4; text-align: center; padding: 16px 0; font-size: 13px; }
`;

function entityTitle(et, e) {
    if (!e) return "-";
    if (et === "evaluator") return [e.firstname, e.lastname].filter(Boolean).join(" ").trim() || "-";
    if (et === "form")      return e.evaluation_name || "-";
    return e.location_name || "-";
}
function entityMeta(et, e) {
    if (!e) return "";
    if (et === "evaluator") return [e.gender, e.province].filter(Boolean).join(" / ");
    if (et === "form")      return [e.evaluation_type, e.division_name].filter(Boolean).join(" / ");
    return e.province || "";
}

export default function DetailModal({ open, entityType, entityId, startDate, endDate, onClose }) {
    const [loading, setLoading] = useState(false);
    const [data, setData]       = useState(null);
    const [err, setErr]         = useState(null);

    useEffect(() => {
        if (!open) return;
        let active = true;
        async function load() {
            setLoading(true); setErr(null); setData(null);
            try {
                const res = await CoreAPI.dashboardHttpService.getUsageDetail({
                    entity_type: entityType, entity_id: entityId,
                    start_date: startDate, end_date: endDate,
                });
                if (active) setData(res);
            } catch (e) {
                if (active) {
                    if (e?.response?.status === 403) setErr("ไม่มีสิทธิ์เข้าถึงข้อมูลนี้");
                    else setErr("เกิดข้อผิดพลาด กรุณาลองใหม่");
                }
            } finally { if (active) setLoading(false); }
        }
        load();
        return () => { active = false; };
    }, [open, entityType, entityId, startDate, endDate]);

    if (!open) return null;
    return (
        <Backdrop onClick={onClose}>
            <Drawer onClick={(e) => e.stopPropagation()}>
                <button className="close" onClick={onClose}>×</button>
                <h2>{entityTitle(entityType, data?.entity)}</h2>
                <div className="meta">{entityMeta(entityType, data?.entity)}</div>
                {loading && <div className="empty">กำลังโหลด...</div>}
                {err && <div className="empty">{err}</div>}
                {data && (
                    <>
                        <div className="tiles">
                            <div className="tile"><div className="l">Submissions</div><div className="v">{(data.summary.submissions ?? 0).toLocaleString("en-US")}</div></div>
                            <div className="tile"><div className="l">Attendances</div><div className="v">{(data.summary.attendances ?? 0).toLocaleString("en-US")}</div></div>
                            <div className="tile">
                                <div className="l">Last Activity</div>
                                <div className="v">{data.summary.last_activity_at ? dayjs(data.summary.last_activity_at).format("DD/MM/BBBB") : "-"}</div>
                            </div>
                        </div>
                        <div className="section-h">การใช้งานรายวัน</div>
                        {data.timeseries?.length ? (
                            <LineChart
                                height={220}
                                xAxis={[{ data: data.timeseries.map(r => r.bucket), scaleType: "point" }]}
                                series={[
                                    { data: data.timeseries.map(r => r.submissions), label: "Submissions", color: "#25368D" },
                                    { data: data.timeseries.map(r => r.attendances), label: "Attendances", color: "#FBE250" },
                                ]}
                                margin={{ left: 40, right: 12, top: 24, bottom: 40 }}
                            />
                        ) : <div className="empty">ไม่มีข้อมูล</div>}
                        <div className="section-h">กิจกรรมล่าสุด</div>
                        <div className="activity">
                            {(data.recent_activity || []).length === 0 && <div className="empty">ไม่มีข้อมูล</div>}
                            {(data.recent_activity || []).map((r, i) => (
                                <div className="row" key={i}>
                                    <span className={`badge ${r.type === "submission" ? "sub" : "att"}`}>
                                        {r.type === "submission" ? "ส่งแบบสอบถาม" : "เข้าใช้บริการ"}
                                    </span>
                                    <span>{dayjs(r.created_at).format("DD/MM/BBBB HH:mm")}</span>
                                    <span style={{ marginLeft: "auto", color: "#505050" }}>{r.label}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Drawer>
        </Backdrop>
    );
}
